import React, { useEffect, useRef, useState } from 'react';
import './BuildARoom.scss';
import Owl from '../UI/Graphics/Owl';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import { BREAKPOINTS } from '../../constants/breakpoints';
import Lottie from 'lottie-web';
import animationData from './OL_Rotation_White-2.json';

const DESKTOP_WIDTH = BREAKPOINTS.large;

/**
 * Renders the build a room component
 * @param {boolean} isFiltering - determines whether the component should display itself
 * @param {object} image - the image src and alt
 * @param {string} title - the title
 * @param {string} description - the description
 * @param {string} ctaText - the cta button text
 * @param {string} ctaLink - the cta button link
 * @param {boolean} showCta
 * @param {('1'|'2'|'3'|'4')} headingType
 * @returns {JSX.Element}
 * @constructor
 */
const BuildARoom = ({
  isFiltering = null,
  image = {},
  title = '',
  description = '',
  ctaText = '',
  ctaLink = '',
  showCta = true,
  headingType = '1',
}) => {
  const animationContainer = useRef();
  const [isDesktop, setIsDesktop] = useState(true);
  const [owlAnimation, setOwlAnimation] = useState({});
  const [animationComplete, setAnimationComplete] = useState(true);

  useEffect(() => {
    setIsDesktop(window.innerWidth > DESKTOP_WIDTH);
    window.addEventListener('resize', handleResize);

    const lottie = Lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData,
    });

    lottie.addEventListener('complete', handleAnimationComplete);

    setOwlAnimation(lottie);

    return () => {
      window.removeEventListener('resize', handleResize);
      lottie.removeEventListener('complete', handleAnimationComplete);
    };
  }, []);

  const handleResize = () => {
    setIsDesktop(window.innerWidth > DESKTOP_WIDTH);
  };

  // Async/await the animation as to not
  // block hover styling on the CTA
  const handleAnimationStart = async () => {
    if (animationComplete) {
      await owlAnimation.goToAndPlay(0);
      setAnimationComplete(false);
    }
  };

  const TitleTag = `h${headingType}`;

  const handleAnimationComplete = () => setAnimationComplete(true);

  return (
    !isFiltering && (
      <div className="c-build-a-room module-background-color-green">
        <div className="c-build-a-room__inner">
          <div className="c-build-a-room__text">
            <div className={`c-build-a-room__title h${headingType}-roobert`}>
              <TitleTag>{title}</TitleTag>
            </div>
            {description && (
              <div
                className="c-build-a-room__description body-1"
                dangerouslySetInnerHTML={{ __html: description }}
              ></div>
            )}
            {showCta && (
              <div className="c-build-a-room__cta-container" onMouseEnter={handleAnimationStart}>
                <CTAButton
                  type={'secondary'}
                  link={ctaLink}
                  text={ctaText}
                  target={'_self'}
                  additionalClasses={'color-white cta'}
                />
              </div>
            )}
            {isDesktop && (
              <div className="c-build-a-room__owl-container">
                <div ref={animationContainer} className="c-build-a-room__animation-container"></div>
              </div>
            )}
          </div>
          <div className="c-build-a-room__image-container">
            <img src={image?.url} alt={image?.alt} />
          </div>
          {!isDesktop && (
            <div className="c-build-a-room__owl-container">
              <Owl />
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default BuildARoom;
